import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CustomFolderFileType } from "src/core/constants/folders";
import { useFolder } from "src/core/contexts/FolderContext";
import {
  useCallEReputationMutation,
  useCreateFolderFileMutation,
  useUpdatePersonDuplicateCheckDoneMutation,
} from "src/core/gql/graphql";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { toast } from "src/core/hooks/use-toast";
import { buildFolderFilesCountRefetch } from "src/core/utils/refetchers";

export const useReexamination = (setOpen: (open: boolean) => void) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { folderFile, isPPH } = useFolder();
  const [createFolderFile] = useCreateFolderFileMutation();
  const [callEReputation] = useCallEReputationMutation();
  const [updatePersonDuplicateCheckDone] = useUpdatePersonDuplicateCheckDoneMutation();

  const paths = useRequiredTenantPaths();
  const navigate = useNavigate();

  const handleReexamination = useCallback(async () => {
    const customerFolderId = folderFile.folderFile?.currentCustomerFolder?.id;
    if (!customerFolderId) {
      toast({
        description: t("global.form.fail"),
        variant: "destructive",
      });
    }

    try {
      setIsLoading(true);

      const { data } = await createFolderFile({
        variables: {
          customerFolderId,
          folderFileTypeCode: isPPH
            ? CustomFolderFileType.MISSION_RENEWAL_PPH
            : CustomFolderFileType.MISSION_RENEWAL,
        },
        refetchQueries: buildFolderFilesCountRefetch(),
        awaitRefetchQueries: true,
      });

      const folderFileId = data?.createFolderFile?.id;

      if (!folderFileId) {
        return;
      }

      const stakeholderIdsToResetDuplicateCheck =
        data?.createFolderFile?.fileCustomerFolder?.stakeholders
          ?.filter((stakeholder) => !stakeholder.isFolderCustomer)
          .map((stakeholder) => stakeholder.entity.id) ?? [];

      if (stakeholderIdsToResetDuplicateCheck.length > 0) {
        await Promise.allSettled(
          stakeholderIdsToResetDuplicateCheck.map((stakeholderId) =>
            updatePersonDuplicateCheckDone({
              variables: {
                id: stakeholderId,
                duplicateCheckDone: "false",
              },
            })
          )
        );
      }

      try {
        await callEReputation({
          variables: {
            folderFileId,
            force: true,
          },
          // En cas d'erreur de callEReputation, on ne renvoie plus vers la page d'erreur.
          context: {
            suppressRedirect: true,
          },
        });
      } catch (error) {
        console.error("[useReexamination::handleReexamination] callEReputation failed", error);
      } finally {
        navigate(paths.folder(folderFileId), { state: { fromReexamination: true } });
      }

      toast({
        description: t("folder.reexamination.success"),
      });
    } catch (error) {
      toast({
        description: t("folder.reexamination.fail"),
        variant: "destructive",
      });
      console.error("Erreur lors renvoi en completion:", error);
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  }, [
    folderFile.folderFile?.currentCustomerFolder?.id,
    createFolderFile,
    navigate,
    setOpen,
    callEReputation,
    t,
    isPPH,
    paths,
    updatePersonDuplicateCheckDone,
  ]);

  return { handleReexamination, isLoading };
};
